import React, { useState } from 'react'
import {ArrowLeft, Upload, X} from 'lucide-react'

interface ETOCalculatorProps {
  onBack: () => void
}

interface SingleData {
  diaJuliano: string
  tmax: string
  tmin: string
  tmean: string
  viento: string
  hr: string
  rs: string
  elevacion: string
  latitud: string
}

interface ResultRow {
  diaJuliano: number
  tmax: number
  tmin: number
  viento: number
  hr: number
  rs: number
  eto: number
}

const ETOCalculator: React.FC<ETOCalculatorProps> = ({ onBack }) => {
  const [singleData, setSingleData] = useState<SingleData>({
    diaJuliano: '150',
    tmax: '25.5',
    tmin: '12.3',
    tmean: '18.9',
    viento: '1.8',
    hr: '72',
    rs: '18.7',
    elevacion: '850',
    latitud: '-16.5'
  })
  const [singleResult, setSingleResult] = useState<number | null>(null)
  const [multipleData, setMultipleData] = useState('150, 25.5, 12.3, 18.9, 1.8, 72, 18.7, 850, -16.5\n151, 26.2, 13.1, 19.6, 2.1, 70, 19.5, 850, -16.5\n152, 24.8, 11.9, 18.3, 1.5, 75, 17.8, 850, -16.5')
  const [results, setResults] = useState<ResultRow[]>([])
  const [error, setError] = useState<string | null>(null)

  // Funciones auxiliares del código VBA original
  const svp = (T: number): number => {
    return 0.6108 * Math.exp((17.27 * T) / (T + 237.3))
  }

  const slopeSaturationVP = (Tmean: number): number => {
    return 4098 * (0.6108 * Math.exp((17.27 * Tmean) / (Tmean + 237.3))) / Math.pow(Tmean + 237.3, 2)
  }

  const atmosphericP = (elevation: number): number => {
    return 101.3 * Math.pow((293 - 0.0065 * elevation) / 293, 5.26)
  }

  const latentHVaporization = (Tm: number): number => {
    return 2.501 - (2.361 * Math.pow(10, -3) * Tm)
  }

  const psychrometricC = (Tmean: number, AtmosfericP: number): number => {
    return 1.013 * Math.pow(10, -3) * AtmosfericP / (latentHVaporization(Tmean) * 0.622)
  }

  const saturationVaporP = (Tmax: number, Tmin: number): number => {
    return (svp(Tmax) + svp(Tmin)) / 2
  }

  const actualVaporP = (RH: number, Tmean: number): number => {
    return RH / 100 * svp(Tmean)
  }

  const radiacionExtraterrestres = (Juliano: number, Latitud: number): number => {
    const pi = Math.PI
    const dr = 1 + 0.033 * Math.cos(2 * pi * Juliano / 365)
    const s = 0.409 * Math.sin(2 * pi * Juliano / 365 - 1.39)
    const Ph = pi / 180 * Latitud
    const Ws = Math.acos(-Math.tan(Ph) * Math.tan(s))
    const Ra = 24 * 60 / pi * 0.082 * dr * (Ws * Math.sin(Ph) * Math.sin(s) + Math.cos(Ph) * Math.cos(s) * Math.sin(Ws))
    return Ra
  }

  const clearSkySR = (Ele: number, Ra: number): number => {
    return (0.75 + 2 * Math.pow(10, -5) * Ele) * Ra
  }

  const netShortwaveR = (A: number, Rs: number): number => {
    return (1 - A) * Rs
  }

  const rnLarga = (Tmax: number, Tmin: number, Ea: number, Rs: number, Rso: number): number => {
    const Sb = 4.903 * Math.pow(10, -9)
    const Tmk4 = Math.pow(Tmax + 273.16, 4)
    const Tmink4 = Math.pow(Tmin + 273.16, 4)
    const a1 = Sb * (Tmk4 + Tmink4) / 2
    const a2 = 0.34 - 0.14 * Math.pow(Ea, 0.5)
    const a3 = 1.35 * Rs / Rso - 0.35
    return a1 * a2 * a3
  }

  const radiationTerm = (slope: number, cs: number, Velocity: number, Rn: number): number => {
    const apoyo = slope + cs * (1 + 0.34 * Velocity)
    return 0.408 * slope * (Rn - 0) / apoyo
  }

  const advectionTerm = (slope: number, cs: number, Velocity: number, T: number, Es: number, Ea: number): number => {
    const apoyo = slope + cs * (1 + 0.34 * Velocity)
    return cs * (900 / (T + 273)) * Velocity * (Es - Ea) / apoyo
  }

  const calculateETO = (data: {
    diaJuliano: number
    tmax: number
    tmin: number
    tmean: number
    viento: number
    hr: number
    rs: number
    elevacion: number
    latitud: number
  }): number => {
    const { diaJuliano, tmax, tmin, tmean, viento, hr, rs, elevacion, latitud } = data
    
    // Validaciones del código VBA original
    if (!diaJuliano || !tmax || !tmin || !tmean || !viento || !hr || !rs || !elevacion || !latitud) {
      return NaN
    }
    
    if (diaJuliano > 366 || diaJuliano <= 0 || elevacion < 0) {
      return NaN
    }
    
    if (tmax < tmin || tmean > 50 || hr > 99 || hr < 2 || viento < 0.2) {
      return NaN
    }
    
    if (tmax === 0 && tmin === 0) {
      return NaN
    }
    
    if (rs > 36 || rs < 1) {
      return NaN
    }
    
    // Cálculos principales
    const A = slopeSaturationVP(tmean)
    const P = atmosphericP(elevacion)
    const cs = psychrometricC(tmean, P)
    const Es = saturationVaporP(tmax, tmin)
    const Ea = actualVaporP(hr, tmean)
    const Ra = radiacionExtraterrestres(diaJuliano, latitud)
    const Rso = clearSkySR(elevacion, Ra)
    const Rns = netShortwaveR(0.23, rs)
    let Rnl = rnLarga(tmax, tmin, Ea, rs, Rso)
    
    if (Rnl <= 0) {
      Rnl = 0
    }
    
    const Rn = Rns - Rnl
    const RT = radiationTerm(A, cs, viento, Rn)
    const AT = advectionTerm(A, cs, viento, tmean, Es, Ea)
    const EToPM = RT + AT
    
    return EToPM
  }

  const handleSingleCalculation = () => {
    setError(null)
    setSingleResult(null)

    // Verificar campos vacíos
    const emptyFields = Object.entries(singleData)
      .filter(([key, value]) => !value)
      .map(([key]) => key)

    if (emptyFields.length > 0) {
      setError(`Por favor complete los siguientes campos: ${emptyFields.join(', ')}`)
      return
    }

    const data = {
      diaJuliano: parseFloat(singleData.diaJuliano),
      tmax: parseFloat(singleData.tmax),
      tmin: parseFloat(singleData.tmin),
      tmean: parseFloat(singleData.tmean),
      viento: parseFloat(singleData.viento),
      hr: parseFloat(singleData.hr),
      rs: parseFloat(singleData.rs),
      elevacion: parseFloat(singleData.elevacion),
      latitud: parseFloat(singleData.latitud)
    }

    // Verificar valores numéricos
    if (Object.values(data).some(isNaN)) {
      setError('Por favor ingrese valores numéricos válidos en todos los campos')
      return
    }

    const eto = calculateETO(data)
    
    if (isNaN(eto)) {
      setError('Los datos ingresados no son válidos para el cálculo. Verifique que: Día Juliano esté entre 1-366, Tmax > Tmin, HR entre 2-99%, Viento ≥ 0.2 m/s, Rs entre 1-36 MJ/m²/día, Elevación ≥ 0')
    } else {
      setSingleResult(eto)
    }
  }

  const handleMultipleCalculation = () => {
    setError(null)
    const lines = multipleData.trim().split('\n')
    const calculatedResults: ResultRow[] = []

    if (lines.length === 0 || (lines.length === 1 && lines[0] === '')) {
      setError('Por favor ingrese datos en el área de texto')
      return
    }

    for (const [index, line] of lines.entries()) {
      const values = line.split(/[,;\t]/).map(v => parseFloat(v.trim()))
      
      if (values.length >= 9 && values.every(v => !isNaN(v))) {
        const [diaJuliano, tmax, tmin, tmean, viento, hr, rs, elevacion, latitud] = values
        const eto = calculateETO({ diaJuliano, tmax, tmin, tmean, viento, hr, rs, elevacion, latitud })
        
        if (!isNaN(eto)) {
          calculatedResults.push({
            diaJuliano,
            tmax,
            tmin,
            viento,
            hr,
            rs,
            eto: parseFloat(eto.toFixed(2))
          })
        } else {
          setError(`Error en la línea ${index + 1}: datos inválidos para el cálculo`)
          return
        }
      } else {
        setError(`Error en la línea ${index + 1}: formato incorrecto. Asegúrese de que tenga 9 valores numéricos`)
        return
      }
    }

    setResults(calculatedResults)
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const text = e.target?.result as string
        setMultipleData(text)
      }
      reader.readAsText(file)
    }
  }

  const clearSingleData = () => {
    setSingleData({
      diaJuliano: '',
      tmax: '',
      tmin: '',
      tmean: '',
      viento: '',
      hr: '',
      rs: '',
      elevacion: '',
      latitud: ''
    })
    setSingleResult(null)
    setError(null)
  }

  const clearMultipleData = () => {
    setMultipleData('')
    setResults([])
    setError(null)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Volver a Herramientas</span>
          </button>
          <div className="text-center">
            <h1 className="text-4xl font-bold text-slate-900 mb-2">
              Calculadora de Evapotranspiración de Referencia (ETo)
            </h1>
            <p className="text-lg text-slate-600">
              Cálculo mediante el método FAO Penman-Monteith - Herramienta para estimación de requerimientos hídricos
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <X className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-3 text-red-400 hover:text-red-500"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Single Calculation */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-slate-900">
              Cálculo de ETo para una fecha específica
            </h2>
            <button
              onClick={clearSingleData}
              className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Limpiar
            </button>
          </div>
          
          <p className="text-slate-600 mb-6">
            Complete los datos requeridos para calcular la evapotranspiración de referencia para un día específico:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Día Juliano (1-366)</label>
              <input
                type="number"
                value={singleData.diaJuliano}
                onChange={(e) => setSingleData({ ...singleData, diaJuliano: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ej: 150"
                min="1"
                max="366"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Tmax (°C)</label>
              <input
                type="number"
                step="0.1"
                value={singleData.tmax}
                onChange={(e) => setSingleData({ ...singleData, tmax: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ej: 25.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Tmin (°C)</label>
              <input
                type="number"
                step="0.1"
                value={singleData.tmin}
                onChange={(e) => setSingleData({ ...singleData, tmin: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ej: 12.3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Tmean (°C)</label>
              <input
                type="number"
                step="0.1"
                value={singleData.tmean}
                onChange={(e) => setSingleData({ ...singleData, tmean: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ej: 18.9"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Viento (m/s) ≥0.2</label>
              <input
                type="number"
                step="0.1"
                value={singleData.viento}
                onChange={(e) => setSingleData({ ...singleData, viento: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ej: 1.8"
                min="0.2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">HR (%) 2-99</label>
              <input
                type="number"
                step="0.1"
                value={singleData.hr}
                onChange={(e) => setSingleData({ ...singleData, hr: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ej: 72"
                min="2"
                max="99"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Rs (MJ/m²/día) 1-36</label>
              <input
                type="number"
                step="0.1"
                value={singleData.rs}
                onChange={(e) => setSingleData({ ...singleData, rs: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ej: 18.7"
                min="1"
                max="36"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Elevación (m) ≥0</label>
              <input
                type="number"
                step="0.1"
                value={singleData.elevacion}
                onChange={(e) => setSingleData({ ...singleData, elevacion: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ej: 850"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Latitud (grados)</label>
              <input
                type="number"
                step="0.1"
                value={singleData.latitud}
                onChange={(e) => setSingleData({ ...singleData, latitud: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ej: -16.5"
              />
            </div>
          </div>

          <button
            onClick={handleSingleCalculation}
            className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-blue-500 to-green-500 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-green-600 transition-all shadow-md hover:shadow-lg"
          >
            Calcular ETo
          </button>

          {singleResult !== null && (
            <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 rounded-lg">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Evapotranspiración de Referencia (ETo):
              </h3>
              <p className="text-4xl font-bold text-blue-600">{singleResult.toFixed(2)} <span className="text-2xl text-slate-600">mm/día</span></p>
              <p className="text-sm text-slate-600 mt-2">
                Este valor representa la cantidad de agua evaporada y transpirada por un cultivo de referencia (pasto) en condiciones específicas.
              </p>
            </div>
          )}
        </div>

        {/* Multiple Calculations */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-slate-900">
              Cálculo de ETo para múltiples fechas
            </h2>
            <button
              onClick={clearMultipleData}
              className="text-sm text-red-500 hover:text-red-700 flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Limpiar
            </button>
          </div>
          
          <p className="text-slate-600 mb-4">
            Pegue sus datos en formato de columnas separadas por comas, tabulaciones o punto y coma:
          </p>
          <div className="bg-blue-50 p-4 rounded-lg mb-4 border border-blue-100">
            <p className="font-semibold text-slate-900 mb-2">Formato esperado:</p>
            <p className="text-sm text-slate-700 mb-2">
              Cada línea debe contener los valores en este orden:
            </p>
            <p className="text-sm font-mono text-slate-600 mb-2">
              DíaJuliano, Tmax(°C), Tmin(°C), Tmean(°C), Viento(m/s), HR(%), Rs(MJ/m²/día), Elevación(m), Latitud(grados)
            </p>
            <p className="text-sm font-mono text-blue-600">
              Ejemplo: 150, 25.5, 12.3, 18.9, 1.8, 72, 18.7, 850, -16.5
            </p>
          </div>

          <div className="mb-4">
            <label className="block w-full cursor-pointer">
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors bg-slate-50 hover:bg-blue-50">
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3 group-hover:text-blue-500" />
                <p className="text-slate-600 mb-1">
                  Haga clic para seleccionar un archivo CSV o TXT, o arrástrelo aquí
                </p>
                <p className="text-sm text-slate-500">Formato: CSV con columnas en el orden especificado</p>
                <input
                  type="file"
                  accept=".csv,.txt"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            </label>
          </div>

          <textarea
            value={multipleData}
            onChange={(e) => setMultipleData(e.target.value)}
            rows={8}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm mb-4"
            placeholder="150, 25.5, 12.3, 18.9, 1.8, 72, 18.7, 850, -16.5&#10;151, 26.2, 13.1, 19.6, 2.1, 68, 19.2, 850, -16.5"
          />

          <button
            onClick={handleMultipleCalculation}
            className="w-full md:w-auto px-8 py-3 bg-gradient-to-r from-blue-500 to-green-500 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-green-600 transition-all shadow-md hover:shadow-lg"
          >
            Calcular Múltiples ETo
          </button>

          {results.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <div className="mb-4 text-sm text-slate-600">
                Mostrando {results.length} resultado(s)
              </div>
              <table className="w-full border-collapse border border-slate-200">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-300 px-4 py-3 text-left font-semibold text-slate-900">Día Juliano</th>
                    <th className="border border-slate-300 px-4 py-3 text-left font-semibold text-slate-900">Tmax (°C)</th>
                    <th className="border border-slate-300 px-4 py-3 text-left font-semibold text-slate-900">Tmin (°C)</th>
                    <th className="border border-slate-300 px-4 py-3 text-left font-semibold text-slate-900">Viento (m/s)</th>
                    <th className="border border-slate-300 px-4 py-3 text-left font-semibold text-slate-900">HR (%)</th>
                    <th className="border border-slate-300 px-4 py-3 text-left font-semibold text-slate-900">Rs (MJ/m²/día)</th>
                    <th className="border border-slate-300 px-4 py-3 text-left font-semibold text-blue-600">ETo (mm/día)</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((row, index) => (
                    <tr key={index} className="hover:bg-slate-50">
                      <td className="border border-slate-300 px-4 py-2">{row.diaJuliano}</td>
                      <td className="border border-slate-300 px-4 py-2">{row.tmax.toFixed(1)}</td>
                      <td className="border border-slate-300 px-4 py-2">{row.tmin.toFixed(1)}</td>
                      <td className="border border-slate-300 px-4 py-2">{row.viento.toFixed(1)}</td>
                      <td className="border border-slate-300 px-4 py-2">{row.hr.toFixed(0)}</td>
                      <td className="border border-slate-300 px-4 py-2">{row.rs.toFixed(1)}</td>
                      <td className="border border-slate-300 px-4 py-2 font-bold text-blue-600">{row.eto.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <div className="mt-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-slate-700">
            <strong>Nota:</strong> Esta herramienta utiliza el método FAO Penman-Monteith para el cálculo de la evapotranspiración de referencia (ETo).
            Los resultados son estimaciones y deben utilizarse con fines de referencia y planificación.
            La fórmula implementada es una conversión directa del código VBA original de HF Riego.
          </p>
        </div>
      </main>
    </div>
  )
}

export default ETOCalculator